import { Injectable } from "@angular/core";

import {
  XLogoInterpreter,
  XLogoParserErrorStrategy,
  ANTLRSyntaxErrorListener,
  AstTranslatorVisitor,
  ProgDeclVerifier,
  SemanticVerifier,
  XLogoSemanticError,
  RuntimeError,
  XLogoSyntaxError,
  SemanticErrorCause,
} from "app/xlogo-parser";
import { XLogoParser, XLogoLexer } from "app/xlogo-parser/parser";
import { ANTLRInputStream, CommonTokenStream, RuleContext } from "antlr4ts";
import { Interval } from "antlr4ts/misc";
import {
  AST,
  ProgDecl,
  Seq,
  BuiltInCommand,
  SpecialBuiltInCommand,
  MakeStmt,
  PrintStmt,
  ProgCallStmt,
  Expr,
  BinaryOpExpr,
  BinaryOp,
  UnaryOp,
  UnaryOpExpr,
} from "app/xlogo-parser/ir/ast";
import { BuiltInActionSet } from "app/xlogo-parser/ir/builtInCommands";
import { DebuggerService } from "./debugger.service";
import { HeaderComponent } from "app/components/header.component";
import { CanvasComponent } from "app/components/canvas.component";
import { SpeedCalculatorService } from "./speedcalculator.service";
import { CanvasService } from "./canvas.service";

enum SourceType {
  INPUT,
  PROGRAMS,
}

export enum DebugDirection {
  FORWARD,
  BACKWARD,
}

@Injectable()
export class InterpreterService {
  /** The maximum amount of time in milliseconds that the interpreter is allowed to execute at full speed.
   * The screen gets updated when this limit exceeds.
   */
  private readonly MAX_MS_TIME_EXECUTE: number = 10;

  private availablePrograms: Map<string, ProgDecl> = new Map();
  private incorrectPrograms: Map<string, ProgDecl> = new Map();
  private executableInput: {
    ast: AST | undefined;
    text: string;
    syntaxError: XLogoSyntaxError[];
    semanticError: XLogoSemanticError[];
  } = { ast: new Seq([]), text: "", syntaxError: [], semanticError: [] };

  private startListeners: ((
    input: string,
    started_successfully: boolean,
    syntaxError: XLogoSyntaxError[],
    semanticError: XLogoSemanticError[]
  ) => void)[] = [];
  private finishListeners: ((
    input: string,
    runtimeError: RuntimeError | undefined
  ) => void)[] = [];
  private debugListener: {
    callback: (interpreter: XLogoInterpreter) => boolean;
    stepFunction: (
      direction: DebugDirection,
      callback: () => void,
      interpreter: XLogoInterpreter
    ) => void;
  } = {
    callback: () => false,
    stepFunction: (
      direction: DebugDirection,
      callback: () => void,
      interpreter: XLogoInterpreter
    ) => {
      if (direction === DebugDirection.FORWARD) callback();
    },
  };

  private currentInterpreter: XLogoInterpreter | undefined;
  private lastStartedParser = {
    [SourceType.INPUT]: 0,
    [SourceType.PROGRAMS]: 0,
  };

  private _debugMode: boolean = false;

  public get debugMode(): boolean {
    return this._debugMode;
  }

  constructor(
    private speedCalculatorService: SpeedCalculatorService,
    private canvasService: CanvasService
  ) {}

  public get CurrentInterpreter(): XLogoInterpreter | undefined {
    return this.currentInterpreter;
  }

  /**
   * Register a function that will be called when the interpreter tries to start. If it is not
   * possible for the interpreter to execute due to syntax or semantic errors the flag started_successfully
   * will be false and one of the lists syntaxError or semanticError will have a length that is non-zero.
   * @param listener Callback that will be notified when the interpreter tries to start
   */
  registerEventListenerStarted(
    listener: (
      input: string,
      started_successfully: boolean,
      syntaxError: XLogoSyntaxError[],
      semanticError: XLogoSemanticError[]
    ) => void
  ): void {
    this.startListeners.push(listener);
  }

  /**
   * Register a function that will be called when the interpreter finishes. The argument
   * input will always be set. The argument runtimeError will only be set if there was an
   * error in the execution. It will be unset if the execution is aborted by the user.
   * @param listener Callback that will be notified when the interpreter exits
   */
  registerEventListenerFinished(
    listener: (input: string, runtimeError: RuntimeError | undefined) => void
  ): void {
    this.finishListeners.push(listener);
  }

  setDebugCallback(
    callback: (interpreter: XLogoInterpreter) => boolean,
    stepFunction: (
      direction: DebugDirection,
      callback: () => void,
      interpreter: XLogoInterpreter
    ) => void
  ) {
    this.debugListener = { callback: callback, stepFunction: stepFunction };
  }

  /**
   * Parses and updates the known programs.
   * @param sourceName Name of the source. Used for the ASTs to know where it originated from. Useful for RuntimeErrors
   * @param code The code that should be parsed
   * @param errorCallback Function to call if there has been syntax or semantic errors
   */
  setPrograms(
    sourceName: string,
    code: string,
    errorCallback: (
      syntaxErrors: XLogoSyntaxError[],
      semanticErrors: XLogoSemanticError[]
    ) => void
  ): void {
    this.lastStartedParser[SourceType.PROGRAMS] = Date.now();
    setTimeout(
      this.parseCode.bind(this),
      100,
      sourceName,
      SourceType.PROGRAMS,
      code,
      errorCallback,
      this.lastStartedParser[SourceType.PROGRAMS]
    );
  }

  /**
   * Parses and updates the input line.
   * @param sourceName Name of the source. Used for the ASTs to know where it originated from. Useful for RuntimeErrors
   * @param code The code that should be parsed
   * @param errorCallback Function to call if there has been syntax or semantic errors
   */
  setInput(
    sourceName: string,
    code: string,
    errorCallback: (
      syntaxErrors: XLogoSyntaxError[],
      semanticErrors: XLogoSemanticError[]
    ) => void,
    successCallback?: () => void
  ): void {
    this.lastStartedParser[SourceType.INPUT] = Date.now();
    setTimeout(
      this.parseCode.bind(this),
      100,
      sourceName,
      SourceType.INPUT,
      code,
      errorCallback,
      this.lastStartedParser[SourceType.INPUT],
      successCallback
    );
  }

  startExecution(actionSets: BuiltInActionSet[], debugMode?: boolean) {
    if (!this.executableInput.ast) {
      this.startListeners.forEach((callback) =>
        setTimeout(
          () =>
            callback(
              this.executableInput.text,
              false,
              this.executableInput.syntaxError,
              this.executableInput.semanticError
            ),
          1
        )
      );
      return;
    }

    if (this.currentInterpreter && this.currentInterpreter.isRunning()) {
      this.stopExecution();
    }

    this._debugMode = debugMode || false;
    this.currentInterpreter = new XLogoInterpreter(
      this.executableInput.ast,
      actionSets,
      this.availablePrograms,
      new Map()
    );

    if (
      this.debugMode &&
      (this.executableInput.ast.rwChildren.length > 1 ||
        (this.executableInput.ast.rwChildren.length === 1 &&
          !(
            this.executableInput.ast.getChildren()[0] instanceof BuiltInCommand
          ) &&
          !(
            this.executableInput.ast.getChildren()[0] instanceof
            SpecialBuiltInCommand
          ) &&
          !(this.executableInput.ast.getChildren()[0] instanceof MakeStmt) &&
          !(this.executableInput.ast.getChildren()[0] instanceof PrintStmt)))
    ) {
      this.debugListener.callback(this.currentInterpreter);
      this.debugListener.callback(this.currentInterpreter);
    } else {
      setTimeout(this.runInterpreter.bind(this), 10);
    }
    this.startListeners.forEach((callback) =>
      setTimeout(() => callback(this.executableInput.text, true, [], []), 1)
    );
  }

  stopExecution(runtimeError?: RuntimeError) {
    if (this.currentInterpreter) {
      this.currentInterpreter.stopExecution();
      this.finishListeners.forEach((callback) =>
        callback(this.executableInput.text, runtimeError)
      );
      this.currentInterpreter = undefined;
    }
    this._debugMode = false;
  }

  resumeExecution() {
    setTimeout(this.runInterpreter.bind(this), 1);
  }

  stepForward() {
    if (!this.currentInterpreter)
      throw Error("Cannot step forward when no program is running!");
    this.debugListener.stepFunction(
      DebugDirection.FORWARD,
      this.runInterpreter.bind(this),
      this.currentInterpreter
    );
  }

  stepBackward() {
    if (!this.currentInterpreter)
      throw Error("Cannot step backwards when no program is running!");
    this.debugListener.stepFunction(
      DebugDirection.BACKWARD,
      this.runInterpreter.bind(this),
      this.currentInterpreter
    );
  }

  /**
   * Parses code and updates the known programs and executable input.
   * @param sourceName Name of the source. Used for the AST to differentiate where it originated.
   * @param sourceType If it is a collection or programs or a sequence of statements that can be executed
   * @param input The code that should be parsed
   * @param errorCallback Function to call when syntax or semantic errors happen
   */
  private async parseCode(
    sourceName: string,
    sourceType: SourceType,
    input: string,
    errorCallback: (
      syntaxErrors: XLogoSyntaxError[],
      semanticErrors: XLogoSemanticError[]
    ) => void,
    startTime: number,
    successCallback?: () => void
  ) {
    if (startTime < this.lastStartedParser[sourceType]) {
      return;
    }
    let collectedSemanticErrors: XLogoSemanticError[] = [];

    // Parse Input and check Syntax
    const inputStream = new ANTLRInputStream(input.toLowerCase());
    const lexer = new XLogoLexer(inputStream);
    lexer.removeErrorListeners();

    const tokenStream = new CommonTokenStream(lexer);
    const parser = new XLogoParser(tokenStream);
    const errorHandler = new XLogoParserErrorStrategy();
    parser.errorHandler = errorHandler;
    parser.removeErrorListeners();
    const syntaxErrorListener = new ANTLRSyntaxErrorListener();
    parser.addErrorListener(syntaxErrorListener);

    let context: RuleContext;
    if (sourceType === SourceType.INPUT) {
      context = parser.inputLines();
    } else {
      context = parser.prog();
    }

    // Translate parsed input to AST
    const visitor = new AstTranslatorVisitor(
      sourceName,
      errorHandler.progsWithErrors
    );

    // Check parsed input semantically
    const semanticVerifier = new SemanticVerifier();

    try {
      let programs: Map<string, ProgDecl> = this.availablePrograms;
      let incorrectPrograms: Map<string, any> = this.incorrectPrograms;
      let astToCheck: AST | AST[] = new Seq([]);

      if (sourceType === SourceType.PROGRAMS) {
        const translatedAST = visitor.visit(context);

        const declVerifier = new ProgDeclVerifier();

        // Check if all declarations are ok
        const verifierOutput = declVerifier.check(translatedAST, undefined);
        if (verifierOutput.errors.length > 0) {
          collectedSemanticErrors = collectedSemanticErrors.concat(
            verifierOutput.errors
          );
        }

        programs = verifierOutput.correctPrograms;
        incorrectPrograms = verifierOutput.erroneousPrograms;

        errorHandler.progsWithErrors.forEach((progName) =>
          incorrectPrograms.set(progName, undefined)
        );
        astToCheck = Array.from(verifierOutput.correctPrograms.values());
      } else {
        if (parser.numberOfSyntaxErrors > 0) {
          this.executableInput = {
            ast: undefined,
            text: input,
            syntaxError: syntaxErrorListener.errorList,
            semanticError: [],
          };
        } else {
          const translatedAST = visitor.visit(context);
          // Check input semantically
          astToCheck = translatedAST;
        }
      }

      // Check body of programs semantically
      let finalResult = semanticVerifier.check(
        astToCheck,
        programs,
        incorrectPrograms
      );

      if (finalResult.errors) {
        // Apply heuristic to handle certain errors

        // Handle unary minus in argument list
        finalResult.errors.forEach((err) => {
          this.checkArgumentsHeuristic(err);
        });
        finalResult = semanticVerifier.check(
          astToCheck,
          programs,
          incorrectPrograms
        );
      }

      if (sourceType === SourceType.PROGRAMS) {
        this.availablePrograms = finalResult.correctPrograms;
        this.incorrectPrograms = finalResult.erroneousPrograms;
      } else {
        if (finalResult.errors.length > 0) {
          collectedSemanticErrors = collectedSemanticErrors.concat(
            finalResult.errors
          );
          this.executableInput = {
            ast: undefined,
            text: input,
            syntaxError: [],
            semanticError: collectedSemanticErrors,
          };
        } else {
          this.executableInput = {
            ast: <AST>astToCheck,
            text: input,
            syntaxError: [],
            semanticError: [],
          };
        }
      }

      if (finalResult.errors.length > 0) {
        collectedSemanticErrors = collectedSemanticErrors.concat(
          finalResult.errors
        );
      }
    } catch (err) {
      console.error(err);
    }

    // If there has been any sort of error, report back using the callback function
    if (parser.numberOfSyntaxErrors > 0 || collectedSemanticErrors.length > 0) {
      errorCallback(syntaxErrorListener.errorList, collectedSemanticErrors);
    } else if (successCallback) {
      successCallback();
    }
  }

  private checkArgumentsHeuristic(err: XLogoSemanticError) {
    if (err.cause === SemanticErrorCause.NOT_ENOUGH_ARGUMENTS) {
      if (
        err.ast instanceof ProgCallStmt ||
        err.ast instanceof BuiltInCommand ||
        err.ast instanceof SpecialBuiltInCommand
      ) {
        let requiredArgs = 0;
        if (err.ast instanceof ProgCallStmt) {
          // Assumption: This code is only executed if the program that should be called is syntactically correct)
          let progDecl =
            this.availablePrograms.get(err.ast.progName) ||
            this.incorrectPrograms.get(err.ast.progName);

          if (!progDecl) return;

          requiredArgs = progDecl.args.length;
        } else {
          requiredArgs = err.ast.requiredArgs;
        }

        for (
          let i = 0;
          i < err.ast.rwChildren.length &&
          err.ast.rwChildren.length < requiredArgs;
          i++
        ) {
          // As the minus expression get stacked we have to go down the tree as long as it is a binary op with minus
          while (true) {
            const problem: Expr = <Expr>err.ast.rwChildren[i];
            if (
              !(
                problem instanceof BinaryOpExpr &&
                problem.operator === BinaryOp.B_MINUS &&
                err.ast.rwChildren.length < requiredArgs
              )
            ) {
              break;
            }
            // Find out if a space is between expr and sign
            // if it is split expr
            const right: Expr = problem.right;
            const left: Expr = problem.left;
            if (
              right.CharStream!.getText(
                new Interval(
                  right.Range!.StartStreamIndex - 1,
                  right.Range!.StartStreamIndex - 1
                )
              ) === UnaryOp.U_MINUS &&
              left.Range!.EndStreamIndex + 1 !==
                right.Range!.StartStreamIndex - 1
            ) {
              const unaryMinus: UnaryOpExpr = new UnaryOpExpr(
                right.Range!,
                right.CharStream,
                right.InputSource!,
                "-",
                right
              );

              err.ast.rwChildren.splice(i, 1, left, unaryMinus);
            } else {
              break;
            }
          }
        }
      }
    }
  }

  private async runInterpreter() {
    if (!this.currentInterpreter) return;

    if (!this.currentInterpreter.isRunning()) {
      this.stopExecution();
      return;
    }

    let delay_ms: number = -1;
    const start_time = Date.now();
    let suspendExecution = false;

    // Execute for MAX_MS_TIME_EXECUTE milliseconds since setTimeout requires a lot of time that slows the interpreter down.
    while (
      delay_ms <= 0.1 &&
      this.currentInterpreter.isRunning() &&
      Date.now() < start_time + this.MAX_MS_TIME_EXECUTE &&
      !suspendExecution
    ) {
      //TODO: HACK. Find a better way. I was in a hurry
      delay_ms = this.speedCalculatorService.calculateCurrentspeed();

      try {
        this.currentInterpreter.continueExecution();
      } catch (err) {
        if (!(err instanceof RuntimeError)) {
          console.error("Unknown Runtime error occurred:");
          console.error(err);
          if (err instanceof Error) {
            err = new RuntimeError(
              "Unknown Runtime error occurred",
              this.currentInterpreter.State.CurrentInstruction,
              "runtime_error_generic",
              { msg: err.message }
            );
          } else {
            err = new RuntimeError(
              "Unknown Runtime error occurred",
              this.currentInterpreter.State.CurrentInstruction,
              "runtime_error_generic",
              { msg: JSON.stringify(err) }
            );
          }
        }
        this.stopExecution(err);
        return;
      }

      if (this.debugMode) {
        suspendExecution = this.debugListener.callback(this.currentInterpreter);
      }
    }

    if (this.currentInterpreter.isPaused())
      delay_ms = Math.max(
        this.currentInterpreter.getPausedMilliseconds(),
        delay_ms
      );

    if (delay_ms > 0 || suspendExecution) {
      //TODO: HACK. Find a better way. I was in a hurry
      this.canvasService.flushCanvas();
    }
    if (!suspendExecution) setTimeout(this.runInterpreter.bind(this), delay_ms);
  }
}



// WEBPACK FOOTER //
// ./src/app/services/interpreter.service.ts